"use client";

import { ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Servicenavigation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    number: "01",
    title: "OTA Revenue Management",
    description: "Maximize your visibility and conversion rates on major platforms. We optimize your listings, manage inventory, and implement dynamic pricing to ensure your property stands out in a crowded digital marketplace.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_management.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_management.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_management.webp-wvc-sizes",
    alt: "Digital dashboard showing hotel OTA performance metrics"
  }, {
    number: "02",
    title: "Hospitality Tech Consulting",
    description: "Navigate the complex landscape of hotel technology. We provide expert recommendations and seamless implementation for PMS, channel managers, and booking engines to streamline your operations.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack.png-wvc-sizes",
    alt: "Modern hotel technology stack integration interface"
  }, {
    number: "03",
    title: "Revenue Audits",
    description: "Uncover hidden revenue opportunities with our comprehensive audits. We analyze your current performance, pricing strategies, and market positioning to deliver actionable insights for growth.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-sizes",
    alt: "Professional revenue audit report on a tablet"
  }];
  return <section data-section-id="2840" id="servicenavigation" className="bg-background text-foreground py-32 md:py-40 lg:py-[10rem] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-[60%_40%] gap-10 lg:gap-16 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <p className="font-default text-[11px] tracking-[0.25em] uppercase mb-8 md:mb-10" style={{
            color: "var(--secondary)"
          }}>
              Our Expertise — 02
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] tracking-tight text-foreground">
              Three Pillars.
              <br />
              One{" "}
              <em className="italic font-default font-light" style={{
              color: "var(--secondary)"
            }}>
                Strategy
              </em>
              .
            </h2>
          </div>
          <div className="flex items-end">
            <p className="font-default text-base md:text-[15px] leading-[1.7] max-w-md" style={{
            color: "var(--muted-foreground)"
          }}>
              At HotelRevBaBa, revenue management is not just a task. It is the 
              strategic foundation beneath every booking, every rate change, and 
              every guest experience. Three pillars, unified into a single, 
              high-conversion growth framework.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 transition-all duration-1000 ease-out delay-200 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {disciplines.map((item, i) => <article data-index={i} key={item.number} className="group relative aspect-[4/5] overflow-hidden border border-border hover:border-secondary transition-all duration-700 cursor-pointer rounded-lg shadow-md">
              {}
              <div className="absolute inset-0 overflow-hidden">
                <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105" />
                {}
                <div className="absolute inset-0 transition-opacity duration-700" style={{
              background: "linear-gradient(180deg, hsla(222, 47%, 7%, 0.4) 0%, hsla(222, 47%, 7%, 0.7) 60%, hsla(222, 47%, 7%, 0.9) 100%)"
            }} />
              </div>

              {}
              <div className="absolute top-8 left-8 md:top-10 md:left-10 z-10">
                <span className="font-default text-[11px] tracking-[0.25em]" style={{
              color: "var(--secondary)"
            }}>
                  {item.number}
                </span>
              </div>

              {}
              <div className="absolute bottom-8 right-8 md:bottom-10 md:right-10 z-10 transition-transform duration-500 group-hover:translate-x-1">
                <ChevronRight className="w-5 h-5" style={{
              color: "var(--secondary)"
            }} strokeWidth={2} />
              </div>

              {}
              <div className="absolute bottom-8 left-8 md:bottom-10 md:left-10 right-16 md:right-20 z-10">
                <h3 className="font-default text-2xl md:text-3xl lg:text-[2rem] leading-tight text-white transition-transform duration-500 ease-out md:group-hover:-translate-y-2" style={{
              fontWeight: 600
            }}>
                  {item.title}
                </h3>
                <p className="font-default text-sm leading-[1.65] mt-4 md:mt-0 md:opacity-0 md:max-h-0 md:overflow-hidden md:group-hover:opacity-100 md:group-hover:max-h-40 md:group-hover:mt-4 transition-all duration-500 ease-out text-white/90">
                  {item.description}
                </p>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}